// The Files view's commit timeline: the points a range can start or end at
// (base, the pull request's commits, commits replaced by force-pushes) and
// the range picker's rules (handles, lanes, span, presets, URL).
import type { ReplacedCommitsResult, TimelineItem } from '../api/queries';

/** Commit metadata from GitHub (timeline items or the replaced-commits lookup). */
export interface CommitMeta {
  oid: string;
  abbreviatedOid: string;
  messageHeadline: string;
  committedDate: string;
  author: { name: string | null; user: { login: string } | null } | null;
}

/**
 * Result of the replaced-commits lookup (`REPLACED_COMMITS`): what the
 * timeline alone cannot tell.
 */
export interface ReplacedLookup {
  /** The pull request's current commits, or null when there are too many to list. */
  current: Set<string> | null;
  /** A force-push's `beforeCommit` → its history, newest first (itself first). */
  history: Map<string, CommitMeta[]>;
  /** Recent commits of the base branch: a history walk skips them. */
  base: Set<string>;
}

/** Most force-pushes (the latest) whose replaced commits are looked up. */
export const MAX_LOOKUP_PUSHES = 10;

/** The before sides of head force-pushes to look up (latest `MAX_LOOKUP_PUSHES`, deduplicated). */
export function forcePushBefores(items: TimelineItem[]): string[] {
  const out: string[] = [];
  for (const it of items)
    if (it.__typename === 'HeadRefForcePushedEvent' && it.beforeCommit && !out.includes(it.beforeCommit.oid)) out.push(it.beforeCommit.oid);
  return out.slice(-MAX_LOOKUP_PUSHES);
}

/** The lookup's answer for `befores` (in the query's `bN` order). */
export function parseReplaced(res: ReplacedCommitsResult, befores: string[]): ReplacedLookup {
  const repo = res.repository;
  const commits = repo?.pullRequest?.commits;
  const history = new Map<string, CommitMeta[]>();
  befores.forEach((b, i) => {
    const nodes = repo?.[`b${i}`]?.history?.nodes;
    if (nodes?.length) history.set(b, nodes);
  });
  return {
    current: commits && commits.totalCount <= commits.nodes.length ? new Set(commits.nodes.map((n) => n.commit.oid)) : null,
    history,
    base: new Set(repo?.base?.history?.nodes.map((n) => n.oid) ?? []),
  };
}

/** Most commits a force-push's history walk adds to the replaced lane. */
export const MAX_REPLACED_PER_PUSH = 10;

export interface RangePoint {
  /** URL value: `base`, or the full commit SHA. */
  value: string;
  sha: string;
  short: string;
  lane: 'main' | 'replaced';
  /** First line of the commit message (the base point: the base branch name). */
  message: string | null;
  author: string | null;
  at: string | null;
  /** Main lane: who force-pushed this head. Replaced lane: whose force-push replaced it. */
  forcePushedBy: string | null;
  /** A force-push precedes this main-lane point (the track breaks before it). */
  breakBefore: boolean;
  head: boolean;
  /** After the viewer's last submitted review. */
  isNew: boolean;
}

export interface RangeTimeline {
  points: RangePoint[];
  /** Point index of the viewer's last reviewed commit, or -1. */
  reviewed: number;
  /** Point index the "Last push" preset starts from, or -1. */
  lastPushFrom: number;
}

interface BuildOpts {
  headRefOid: string;
  baseRefOid: string;
  baseRefName: string;
  viewer: string;
  lookup?: ReplacedLookup | null;
}

type CommitItem = Extract<TimelineItem, { __typename: 'PullRequestCommit' }>;
type ForcePush = Extract<TimelineItem, { __typename: 'HeadRefForcePushedEvent' | 'BaseRefForcePushedEvent' }>;
type Review = Extract<TimelineItem, { __typename: 'PullRequestReview' }>;

function authorOf(c: CommitMeta): string | null {
  return c.author?.user?.login ?? c.author?.name ?? null;
}

/** The viewer's last submitted review that names a commit. */
export function lastViewerReview(items: TimelineItem[], viewer: string): Review | null {
  if (!viewer) return null;
  let last: Review | null = null;
  for (const it of items) {
    if (it.__typename !== 'PullRequestReview' || it.state === 'PENDING' || !it.reviewCommit) continue;
    if (it.author?.login !== viewer) continue;
    const at = it.submittedAt ?? it.createdAt;
    if (!last || at >= (last.submittedAt ?? last.createdAt)) last = it;
  }
  return last;
}

/**
 * Timeline points, oldest first: base, then the pull request's commits in
 * timeline order on the main lane. Each head force-push breaks the track;
 * the commits it replaced (no longer in the pull request) sit on the
 * replaced lane just before the break: the `beforeCommit`, and, when the
 * lookup has it, the rest of its history down to commits still in the pull
 * request or on the base branch (at most `MAX_REPLACED_PER_PUSH`). Without
 * the lookup, a commit counts as replaced when a later force-push names it
 * as `beforeCommit`.
 */
export function buildTimeline(items: TimelineItem[], opts: BuildOpts): RangeTimeline {
  const { headRefOid, lookup } = opts;
  const commitAt = new Map<string, number>();
  items.forEach((it, i) => {
    if (it.__typename === 'PullRequestCommit' && !commitAt.has(it.commit.oid)) commitAt.set(it.commit.oid, i);
  });
  // Last force-push naming each SHA as its before side.
  const replacedAt = new Map<string, number>();
  items.forEach((it, i) => {
    if (it.__typename === 'HeadRefForcePushedEvent' && it.beforeCommit) replacedAt.set(it.beforeCommit.oid, i);
  });
  // In the pull request now: the head; per the lookup when it listed every
  // commit; else a timeline commit no later force-push replaced.
  const isCurrent = (oid: string): boolean => {
    if (oid === headRefOid) return true;
    if (lookup?.current) return lookup.current.has(oid);
    const at = commitAt.get(oid);
    if (at === undefined) return false;
    const r = replacedAt.get(oid);
    return r === undefined || r < at;
  };

  const points: RangePoint[] = [
    {
      value: 'base',
      sha: opts.baseRefOid,
      short: opts.baseRefOid.slice(0, 7),
      lane: 'main',
      message: opts.baseRefName,
      author: null,
      at: null,
      forcePushedBy: null,
      breakBefore: false,
      head: false,
      isNew: false,
    },
  ];
  const placed = new Set<string>();
  let pendingBreak = false;
  const review = lastViewerReview(items, opts.viewer);
  const pushedBy = new Map<string, string | null>();

  const addMain = (p: Omit<RangePoint, 'lane' | 'breakBefore' | 'head' | 'isNew' | 'value'>) => {
    placed.add(p.sha);
    points.push({ ...p, value: p.sha, lane: 'main', breakBefore: pendingBreak, head: p.sha === headRefOid, isNew: false });
    pendingBreak = false;
  };
  const addReplaced = (p: Omit<RangePoint, 'lane' | 'breakBefore' | 'head' | 'isNew' | 'value'>) => {
    placed.add(p.sha);
    points.push({ ...p, value: p.sha, lane: 'replaced', breakBefore: false, head: false, isNew: false });
  };
  const fromMeta = (c: CommitMeta, by: string | null) => ({
    sha: c.oid,
    short: c.abbreviatedOid,
    message: c.messageHeadline,
    author: authorOf(c),
    at: c.committedDate,
    forcePushedBy: by,
  });

  items.forEach((it, i) => {
    if (it.__typename === 'PullRequestCommit') {
      const c = (it as CommitItem).commit;
      if (placed.has(c.oid) || commitAt.get(c.oid) !== i) return;
      if (isCurrent(c.oid)) addMain(fromMeta(c, null));
      else {
        const by = items[replacedAt.get(c.oid) ?? -1] as ForcePush | undefined;
        addReplaced(fromMeta(c, by?.actor?.login ?? null));
      }
    } else if (it.__typename === 'HeadRefForcePushedEvent') {
      const fp = it as ForcePush;
      const by = fp.actor?.login ?? null;
      if (fp.afterCommit) pushedBy.set(fp.afterCommit.oid, by);
      const before = fp.beforeCommit;
      if (before) {
        const walk = lookup?.history.get(before.oid);
        const stop = (oid: string) => isCurrent(oid) || oid === opts.baseRefOid || !!lookup?.base.has(oid);
        const group: CommitMeta[] = [];
        if (walk?.length) {
          for (const c of walk) {
            if (group.length >= MAX_REPLACED_PER_PUSH) break;
            if (stop(c.oid) || placed.has(c.oid)) continue;
            group.push(c);
          }
          group.reverse();
          for (const c of group) addReplaced(fromMeta(c, by));
        } else if (!placed.has(before.oid) && !isCurrent(before.oid)) {
          addReplaced({ sha: before.oid, short: before.abbreviatedOid, message: null, author: null, at: null, forcePushedBy: by });
        }
      }
      pendingBreak = true;
    } else if (it.__typename === 'PullRequestReview' && it === review) {
      // A review on a commit the timeline no longer lists: show it where the review was made.
      const oid = review.reviewCommit!.oid;
      if (!placed.has(oid) && !commitAt.has(oid) && !isCurrent(oid))
        addReplaced({ sha: oid, short: oid.slice(0, 7), message: null, author: null, at: null, forcePushedBy: null });
    }
  });
  if (headRefOid && !placed.has(headRefOid))
    addMain({ sha: headRefOid, short: headRefOid.slice(0, 7), message: null, author: null, at: null, forcePushedBy: null });

  for (const p of points) if (p.lane === 'main' && pushedBy.has(p.sha)) p.forcePushedBy = pushedBy.get(p.sha) ?? 'someone';
  const reviewed = review ? points.findIndex((p) => p.value === review.reviewCommit!.oid) : -1;
  if (reviewed >= 0) for (let i = reviewed + 1; i < points.length; i++) points[i].isNew = true;

  return { points, reviewed, lastPushFrom: lastPushFrom(items, points, headRefOid) };
}

/**
 * Where "Last push" starts: the previous head. When the head came from a
 * force-push, that push's `beforeCommit`; otherwise the main-lane point
 * before the head (GitHub does not report plain pushes, so each commit
 * counts as one push).
 */
function lastPushFrom(items: TimelineItem[], points: RangePoint[], head: string): number {
  const headIdx = points.findIndex((p) => p.head);
  if (headIdx <= 0) return -1;
  let lastFp: ForcePush | null = null;
  for (const it of items) if (it.__typename === 'HeadRefForcePushedEvent') lastFp = it as ForcePush;
  // The head is still what the last force-push left: it started from that push's before side.
  if (lastFp?.afterCommit?.oid === head && lastFp.beforeCommit) {
    const before = lastFp.beforeCommit.oid;
    const i = points.findIndex((p) => p.sha === before && p.value !== 'base');
    if (i >= 0) return i;
  }
  for (let i = headIdx - 1; i >= 0; i--) if (points[i].lane === 'main') return i;
  return -1;
}

// ---------------------------------------------------------------- the range

/**
 * The two picked points: `from` (handle A, the diff's old side) and `to`
 * (handle B, the new side). Any two distinct points, on either lane, in
 * either order: the diff shows from → to as picked.
 */
export interface Range {
  from: number;
  to: number;
}
export type Handle = 'from' | 'to';

/**
 * Point index of a URL value (`from`/`to`), or -1 when it is not on the
 * timeline. `<sha>^` (a commit's parent, from a GitHub single-commit URL)
 * is the point before that commit on its lane.
 */
export function indexOf(points: RangePoint[], value: string): number {
  if (value.endsWith('^')) {
    const i = points.findIndex((p) => p.value === value.slice(0, -1));
    if (i < 0) return -1;
    for (let j = i - 1; j >= 0; j--) if (points[j].lane === points[i].lane) return j;
    return -1;
  }
  return points.findIndex((p) => p.value === value);
}

/** The range the URL selects: `from` absent = base, `to` absent = head. */
export function rangeFromQuery(points: RangePoint[], q: URLSearchParams): { range: Range; from: string; to: string } {
  const head = points.find((p) => p.head);
  const from = q.get('from') ?? 'base';
  const to = q.get('to') ?? head?.value ?? '';
  return { range: { from: indexOf(points, from), to: indexOf(points, to) }, from, to };
}

/** Query params for a range (`null` deletes: `from` defaults to base, `to` to head). */
export function rangeToQuery(points: RangePoint[], r: Range): { from: string | null; to: string | null } {
  const a = points[r.from];
  const b = points[r.to];
  return { from: !a || a.value === 'base' ? null : a.value, to: !b || b.head ? null : b.value };
}

/** A and B traded places. */
export function swap(r: Range): Range {
  return { from: r.to, to: r.from };
}

/** Put `handle` on point `i`; landing on the other handle's point changes nothing. */
export function setHandle(r: Range, handle: Handle, i: number): Range {
  const other = handle === 'from' ? r.to : r.from;
  if (i === other || i < 0) return r;
  return handle === 'from' ? { from: i, to: r.to } : { from: r.from, to: i };
}

/**
 * Keyboard move of a handle. ←/→ step to the previous or next point on the
 * handle's lane (stepping over the other handle), Home/End jump to the
 * lane's first or last free point, ↑/↓ switch to the replaced (above) or
 * main lane at the nearest column (ties go right, toward newer). Null for
 * other keys; the range unchanged when there is nowhere to go.
 */
export function keyMove(points: RangePoint[], r: Range, handle: Handle, key: string): Range | null {
  const at = handle === 'from' ? r.from : r.to;
  const other = handle === 'from' ? r.to : r.from;
  const here = points[at];
  if (!here) return ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key) ? r : null;
  const lane = here.lane;
  const free = (i: number, l: RangePoint['lane']) => i !== other && points[i].lane === l;
  let to = -1;
  if (key === 'ArrowLeft') {
    for (let i = at - 1; i >= 0 && to < 0; i--) if (free(i, lane)) to = i;
  } else if (key === 'ArrowRight') {
    for (let i = at + 1; i < points.length && to < 0; i++) if (free(i, lane)) to = i;
  } else if (key === 'Home') {
    for (let i = 0; i < points.length && to < 0; i++) if (free(i, lane)) to = i;
  } else if (key === 'End') {
    for (let i = points.length - 1; i >= 0 && to < 0; i--) if (free(i, lane)) to = i;
  } else if (key === 'ArrowUp' || key === 'ArrowDown') {
    const target: RangePoint['lane'] = key === 'ArrowUp' ? 'replaced' : 'main';
    if (target === lane) return r;
    let best = Infinity;
    points.forEach((_, i) => {
      if (!free(i, target)) return;
      const d = Math.abs(i - at);
      if (d < best || (d === best && i > at)) {
        best = d;
        to = i;
      }
    });
  } else return null;
  return to < 0 ? r : setHandle(r, handle, to);
}

/**
 * The track from base to point `i`, as drawn: the main-lane points up to it,
 * or for a replaced point, the main-lane points up to the one its run forks
 * off, then the run's points up to it.
 */
export function trackPath(points: RangePoint[], i: number): number[] {
  if (!points[i]) return [];
  let root = i;
  const run: number[] = [];
  if (points[i].lane === 'replaced') {
    let k = i;
    while (k >= 0 && points[k].lane === 'replaced') run.unshift(k--);
    run.splice(run.indexOf(i) + 1);
    root = k;
  }
  const main: number[] = [];
  for (let k = 0; k <= root; k++) if (points[k].lane === 'main') main.push(k);
  return [...main, ...run];
}

export interface Span {
  /** Points the span covers, both ends included. */
  points: Set<number>;
  /** Consecutive point pairs to highlight along the track. */
  edges: [number, number][];
  /** One end lies on the other's track (no detour through a fork point). */
  oneLine: boolean;
  /** Steps along the track between the two ends. */
  apart: number;
}

/**
 * The stretch of track between A and B: from each end back to the last
 * point their tracks share. On one line that is the points between them;
 * across lanes it runs through the fork point.
 */
export function spanOf(points: RangePoint[], r: Range): Span | null {
  if (!points[r.from] || !points[r.to] || r.from === r.to) return null;
  const a = trackPath(points, r.from);
  const b = trackPath(points, r.to);
  let common = 0;
  while (common < a.length && common < b.length && a[common] === b[common]) common++;
  const set = new Set<number>();
  const edges: [number, number][] = [];
  for (const path of [a, b]) {
    for (let k = common - 1; k < path.length; k++) {
      set.add(path[k]);
      if (k >= common) edges.push([path[k - 1], path[k]]);
    }
  }
  const oneLine = common === a.length || common === b.length;
  return { points: set, edges, oneLine, apart: a.length - common + (b.length - common) };
}

export interface Preset {
  id: 'all' | 'last-push' | 'since-review';
  label: string;
  range: Range | null;
  /** Why the preset is unavailable (shown as its tooltip). */
  disabled: string | null;
}

export function presets(t: RangeTimeline): Preset[] {
  const head = t.points.findIndex((p) => p.head);
  const all: Preset = { id: 'all', label: 'All', range: head > 0 ? { from: 0, to: head } : null, disabled: head > 0 ? null : 'No commits yet' };
  const last: Preset = {
    id: 'last-push',
    label: 'Last push',
    range: t.lastPushFrom >= 0 ? { from: t.lastPushFrom, to: head } : null,
    disabled: t.lastPushFrom >= 0 ? null : 'No earlier head to compare with',
  };
  let since: Preset;
  if (t.reviewed < 0) since = { id: 'since-review', label: 'Since my last review', range: null, disabled: 'You have not submitted a review on this pull request' };
  else if (t.reviewed === head) since = { id: 'since-review', label: 'Since my last review', range: null, disabled: 'Nothing new since your last review' };
  else since = { id: 'since-review', label: 'Since my last review', range: { from: t.reviewed, to: head }, disabled: null };
  return [all, last, since];
}

/** Short label of a range end: `base`, `head`, or the short SHA. */
export function endLabel(p: RangePoint | undefined, value: string): string {
  if (!p) return value === 'base' ? 'base' : value.slice(0, 7);
  return p.value === 'base' ? 'base' : p.head ? 'head' : p.short;
}

/**
 * One-line summary: "A base → B head · 4 commits apart", or "· different
 * lanes" when neither end is on the other's track.
 */
export function rangeSummary(points: RangePoint[], r: Range, from: string, to: string): string {
  const text = `A ${endLabel(points[r.from], from)} → B ${endLabel(points[r.to], to)}`;
  const span = spanOf(points, r);
  if (!span) return text;
  if (!span.oneLine) return `${text} · different lanes`;
  return `${text} · ${span.apart} commit${span.apart === 1 ? '' : 's'} apart`;
}

/** A is later on the timeline than B: the diff runs backwards in time. */
export function reversed(r: Range): boolean {
  return r.from >= 0 && r.to >= 0 && r.from > r.to;
}

/** Tooltip text of a point. */
export function pointTitle(p: RangePoint): string {
  if (p.value === 'base') return `base · ${p.message ?? ''}${p.short ? ' @ ' + p.short : ''}`;
  const parts = [p.short];
  if (p.message) parts.push(p.message);
  if (p.author) parts.push(p.author);
  if (p.lane === 'replaced') parts.push(p.forcePushedBy ? `replaced by force-push by ${p.forcePushedBy}` : 'replaced by force-push');
  else if (p.forcePushedBy) parts.push(`force-pushed by ${p.forcePushedBy}`);
  if (p.head) parts.push('head');
  if (p.isNew) parts.push('new since your last review');
  return parts.join(' · ');
}
